import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import SplitText from '../components/effects/SplitText';
import DecryptedText from '../components/effects/DecryptedText';
import Aurora from '../components/backgrounds/Aurora';
import HeroParticles from '../components/HeroParticles';
import MissionConsole from '../components/MissionConsole';
import JneuralTerminal from '../components/JneuralTerminal';
import './css/Home.css';

gsap.registerPlugin(ScrollTrigger);

/* ─── Inline SVG atoms ─── */
const ArrowNE = ({ className = '' }) => (
  <svg className={className} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="3" y1="13" x2="13" y2="3" />
    <polyline points="6,3 13,3 13,10" />
  </svg>
);

const IconGitHub = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 .5C5.65.5.5 5.65.5 12c0 5.09 3.293 9.407 7.862 10.938.574.103.784-.25.784-.554 0-.272-.009-1.042-.015-1.9-3.197.695-3.874-1.374-3.874-1.374-.523-1.33-1.276-1.685-1.276-1.685-1.044-.714.079-.7.079-.7 1.153.08 1.76 1.184 1.76 1.184 1.026 1.757 2.693 1.25 3.35.956.104-.742.4-1.25.729-1.538-2.553-.29-5.237-1.277-5.237-5.684 0-1.254.449-2.282 1.183-3.086-.118-.29-.512-1.46.113-3.042 0 0 .964-.308 3.16 1.178a11.04 11.04 0 012.877-.388c.976.004 1.96.132 2.877.388 2.193-1.486 3.156-1.178 3.156-1.178.627 1.582.233 2.752.114 3.042.737.804 1.182 1.832 1.182 3.086 0 4.419-2.688 5.39-5.251 5.674.413.354.78 1.059.78 2.135 0 1.538-.014 2.778-.014 3.156 0 .307.208.665.79.552C20.21 21.4 23.5 17.087 23.5 12 23.5 5.65 18.35.5 12 .5z" />
  </svg>
);

const IconLinkedIn = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

/* Technical compass-rose reticle — retired, kept removed from hero. Removed entirely. */

/* ─── Data ─── */
const SERVICES = [
  {
    num: '01', title: 'Autonomous Systems',
    desc: 'Real-time control architectures for UAVs, UGVs, and USVs — from mission planning to adaptive execution in unstructured, GNSS-denied environments.',
    tags: ['ROS2', 'Navigation', 'Path Planning', 'Control Systems'],
  },
  {
    num: '02', title: 'Simulation & Modelling',
    desc: 'High-fidelity simulation for algorithms validation, CFD-based aerodynamic design, and hardware-in-the-loop testing pipelines before live deployment.',
    tags: ['CFD', 'HIL Testing', 'Physics Sim', 'Validation'],
  },
  {
    num: '03', title: 'AI & Perception',
    desc: 'On-device inference for computer vision, sensor fusion, and state estimation. Designed for GPS-denied corridors where cloud latency is unacceptable.',
    tags: ['Computer Vision', 'ML / Inference', 'LiDAR', 'Sensor Fusion'],
  },
  {
    num: '04', title: 'Embedded Firmware',
    desc: 'Deterministic C/C++ for flight controllers, sensor drivers, and safety-critical systems where timing guarantees are a hard requirement, not a goal.',
    tags: ['C / C++', 'RTOS', 'Sensor Drivers', 'Real-Time'],
  },
  {
    num: '05', title: 'Communication Systems',
    desc: 'Mesh networking, ELRS long-range links, and LTE / 5G fallback for resilient multi-platform coordination across contested or degraded environments.',
    tags: ['ELRS', 'Mesh Networking', 'LTE Fallback', 'Protocols'],
  },
  {
    num: '06', title: 'Data & Infrastructure',
    desc: 'Telemetry pipelines, mission dashboards, and enterprise-grade storage for operational data at scale — built to run on hardware you own.',
    tags: ['Telemetry', 'Dashboards', 'Data Pipelines', 'Self-Hosted'],
  },
];

const PROJECTS = [
  {
    num: '01', title: 'Mission Decision Engine — Dissertation', category: 'Autonomy / Mission Systems',
    desc: 'Final-year dissertation: a GNSS-denied UAV firmware that is more than autonomous flight. An on-board compute node (Jetson Orin Nano, ROS2) fuses LiDAR SLAM, optical flow and IMU pre-integration with a modular under-belly payload bay (surveillance, drop units, LiDAR) to execute and re-plan missions in real time — flight management, ISR, and payload control from a single decision engine.',
    tags: ['ROS2', 'LiDAR SLAM', 'Jetson Orin Nano', 'Mission Systems'],
    image: '/images/DroneLidarMapping.png',
  },
  {
    num: '02', title: 'Lockheed Martin — Placement Year', category: 'Defence Software',
    desc: 'Placement year in a UK defence engineering team working on simulation and embedded software for military programmes. Contributed to multiple active projects across the placement, won 1st place at the 2025 Global LMCO Hackathon, and closed the year with a full-time return offer.',
    tags: ['C / C++', 'Simulation', 'Embedded Systems', 'Defence'],
    image: '/images/NetworkVisualComplex.jpg',
  },
  {
    num: '03', title: 'PSO Flight Optimisation', category: 'Simulation & AI',
    desc: 'Particle Swarm Optimisation engine for autonomous PID controller tuning. Multi-dimensional fitness landscape convergence eliminates manual parameter guessing and allows sim-to-real transfer without flight testing.',
    tags: ['Python', 'PSO', 'Simulation', 'Control Theory'],
    image: '/images/MultiColouredOptimisationCurve.jpg',
  },
  {
    num: '04', title: 'FPV Combat Drone', category: 'Hardware Build',
    desc: 'Custom 5" freestyle and FPV combat drone built from component level. ExpressLRS long-range radio, custom power distribution, Betaflight tuned for aggressive flight characteristics and sub-10ms latency.',
    tags: ['ELRS', 'Betaflight', 'PCB Design', 'RF Systems'],
    image: '/images/5InchDroneBuild1.jpg',
  },
  {
    num: '05', title: 'OverwatchATAC Ground Station', category: 'Ground Systems',
    desc: 'C++ ground control station for the modular UAV: real-time MAVLink telemetry, map-based mission planning, multi-vehicle coordination, modular payload command channels, and an encrypted uplink. Built to be operated in the field, not on a desk.',
    tags: ['C++', 'MAVLink', 'Real-Time', 'Encryption'],
    image: '/images/MotorwayCrossJunction.png',
  },
  {
    num: '06', title: 'jneural Infrastructure', category: 'Self-Hosted Systems',
    desc: 'Self-hosted server stack running mission-critical workloads: containerised web apps, market-data terminal, internal GitLab, GPU inference queue and a custom admin panel with anomaly detection and automated threat response.',
    tags: ['Docker', 'Caddy', 'Cloudflare Tunnel', 'Self-Hosted GitLab'],
    image: '/images/CustomSpecalisedServers.jpg',
  },
];

const PLATFORMS = [
  {
    id: 'mission-decision-engine',
    name: 'Mission Decision Engine',
    subtitle: 'GNSS-Denied Modular UAV Firmware',
    type: 'Autonomy / Mission Systems',
    desc: 'On-board compute firmware for a GNSS-denied UAV — more than autonomous flight. Fuses live aircraft state with modular payloads (surveillance, custom drop units, LiDAR) carried on a standardised under-belly bay to execute and re-plan missions in real time: flight management, ISR, surveillance, and payload control from a single mission decision engine.',
    specs: ['Modular under-belly payload bay', 'On-board compute (Jetson Orin Nano)', 'GNSS-denied SLAM + sensor fusion', 'Real-time mission re-planning'],
    image: '/images/DroneLidarMapping.png',
  },
  {
    id: 'thermal-isr',
    name: 'Thermal ISR Payload',
    subtitle: 'Multi-Spectral Gimbal Module',
    type: 'Surveillance / Detection',
    desc: 'Thermal-equipped gimbal payload designed to slot into the Mission Decision Engine\'s under-belly bay. Radiometric FLIR delivers real-time heat signatures through darkness and obstructed views, with on-board AI detection running at the edge so decisions happen on the aircraft, not in the cloud.',
    specs: ['Radiometric FLIR sensor', 'AI edge detection', 'All-weather / night-capable', 'Hot-swappable on platform'],
    image: '/images/GroundMovementThermalWhiteHot.png',
  },
  {
    id: 'overwatch-atac',
    name: 'OverwatchATAC',
    subtitle: 'C++ Ground Control Station',
    type: 'Ground Systems',
    desc: 'Cross-platform C++ ground control software paired with the Mission Decision Engine. Real-time MAVLink telemetry, map-based mission planning, multi-vehicle coordination, modular payload command channels, and an encrypted uplink — built to be operated in the field, not on a desk.',
    specs: ['Real-time MAVLink telemetry', 'Map-based mission planning', 'Multi-vehicle coordination', 'Encrypted command uplink'],
    image: '/images/MotorwayCrossJunction.png',
  },
];

/* ─── Component ─── */
const Home = () => {
  const platformsRef = useRef(null);
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [formStatus, setFormStatus] = useState(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      /* Hero entrance */
      gsap.timeline({ defaults: { ease: 'power4.out' } })
        .from('.hero__label', { y: 28, opacity: 0, duration: 0.8, delay: 0.25 })
        .from('.hero__tagline', { y: 24, opacity: 0, duration: 0.7 }, '-=0.3')
        .from('.hero__desc', { y: 20, opacity: 0, duration: 0.6 }, '-=0.4')
        .from('.hero__actions', { y: 20, opacity: 0, duration: 0.6 }, '-=0.4')
        .from('.hero__scroll', { opacity: 0, duration: 0.9 }, '-=0.6');

      /* Hero aurora parallax */
      gsap.to('.hero__aurora', {
        yPercent: 18, ease: 'none',
        scrollTrigger: { trigger: '.hero', start: 'top top', end: 'bottom top', scrub: true },
      });

      /* About — heading clip-path wipe */
      gsap.fromTo('.about__heading',
        { clipPath: 'inset(0 0 100% 0)' },
        {
          clipPath: 'inset(0 0 0% 0)', duration: 1.2, ease: 'power3.out',
          scrollTrigger: { trigger: '.about__heading', start: 'top 85%' },
        }
      );
      gsap.from('.about__tag', { y: 16, opacity: 0, duration: 0.6, scrollTrigger: { trigger: '.about', start: 'top 80%' } });
      gsap.from('.about__body p', {
        y: 36, opacity: 0, duration: 0.8, stagger: 0.15, ease: 'power3.out',
        scrollTrigger: { trigger: '.about__body', start: 'top 82%' },
      });

      /* About stats count-up */
      gsap.utils.toArray('.stat-num').forEach((el) => {
        const target = parseInt(el.dataset.target, 10);
        gsap.fromTo(el,
          { textContent: 0 },
          {
            textContent: target, duration: 1.8, ease: 'power2.out',
            snap: { textContent: 1 },
            scrollTrigger: { trigger: el, start: 'top 85%' },
          }
        );
      });

      /* Services rows slide in */
      gsap.utils.toArray('.service-row').forEach((row, i) => {
        gsap.from(row, {
          x: 50, opacity: 0, duration: 0.7, ease: 'power3.out', delay: (i % 3) * 0.06,
          scrollTrigger: { trigger: row, start: 'top 92%' },
        });
      });

      /* Project cards */
      gsap.utils.toArray('.project-card').forEach((card) => {
        const vis = card.querySelector('.project-card__vis');
        const body = card.querySelector('.project-card__body');
        const img = card.querySelector('.project-card__img');

        if (vis) {
          gsap.from(vis, { scale: 0.93, opacity: 0, duration: 1, ease: 'power3.out', scrollTrigger: { trigger: card, start: 'top 87%' } });
        }
        if (body) {
          gsap.from(body, { opacity: 0, y: 28, duration: 0.9, ease: 'power3.out', scrollTrigger: { trigger: card, start: 'top 82%' } });
        }
        if (img) {
          gsap.to(img, {
            yPercent: -8, ease: 'none',
            scrollTrigger: { trigger: card, start: 'top bottom', end: 'bottom top', scrub: true },
          });
        }
      });

      /* Platforms horizontal scroll — desktop only */
      if (platformsRef.current && window.innerWidth >= 768) {
        const track = platformsRef.current.querySelector('.platforms-track');
        const progress = platformsRef.current.querySelector('.platforms-progress');
        if (track) {
          gsap.to(track, {
            x: () => -(track.scrollWidth - window.innerWidth),
            ease: 'none',
            scrollTrigger: {
              trigger: platformsRef.current,
              start: 'top top',
              end: () => `+=${track.scrollWidth - window.innerWidth}`,
              pin: true,
              scrub: 0.5,
              invalidateOnRefresh: true,
              onUpdate: (self) => {
                if (progress) progress.style.width = `${self.progress * 100}%`;
              },
            },
          });
        }
      }

      /* Contact heading */
      gsap.fromTo('.contact__heading',
        { filter: 'blur(10px)', opacity: 0, y: 28 },
        {
          filter: 'blur(0px)', opacity: 1, y: 0, duration: 1.2, ease: 'power3.out',
          scrollTrigger: { trigger: '.contact__heading', start: 'top 78%' },
        }
      );
      gsap.from('.contact-info__row', {
        y: 24, opacity: 0, duration: 0.7, stagger: 0.1, ease: 'power3.out',
        scrollTrigger: { trigger: '.contact-info', start: 'top 82%' },
      });
      gsap.from('.contact-form .form-group', {
        y: 24, opacity: 0, duration: 0.7, stagger: 0.09, ease: 'power3.out',
        scrollTrigger: { trigger: '.contact-form', start: 'top 82%' },
      });
    });

    return () => ctx.revert();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormStatus('sending');
    setTimeout(() => {
      setFormStatus('sent');
      setFormData({ name: '', email: '', message: '' });
      setTimeout(() => setFormStatus(null), 4000);
    }, 1000);
  };

  return (
    <main className="portfolio">

      {/* ══ HERO ══ */}
      <section
        id="hero"
        className="hero"
        data-section-theme="hero"
        style={{
          '--nav-bg': 'rgba(8,10,14,0.55)',
          '--nav-text': '#f5f2ec',
          '--nav-accent': '#3d5af1',
          '--nav-border': 'rgba(245,242,236,0.08)',
          '--section-bg': '#080a0e',
        }}
      >
        <div className="hero__aurora">
          <Aurora colorStops={['#080a0e', '#0c1828', '#1a3460']} amplitude={0.65} blend={0.5} speed={0.4} />
        </div>
        <div className="hero__grain" aria-hidden="true" />
        {/* Reticle removed — see HeroParticles for the new interactive layer */}
        <HeroParticles />

        <div className="hero__content">
          <span className="hero__label">
            <DecryptedText text="Software Engineer — Autonomous Systems" trigger="mount" speed={30} />
          </span>
          <h1 className="hero__title">
            <SplitText type="words" animation="fadeUp" stagger={0.07}>Josh Lake</SplitText>
          </h1>
          <p className="hero__tagline">Building systems that operate in the real world.</p>
          <p className="hero__desc">
            Autonomous platforms, edge intelligence, and mission-critical software —
            engineered from the ground up with operational readiness at its core.
          </p>
          <div className="hero__actions">
            <a href="#work" className="btn-primary">
              <span>View Work</span>
              <ArrowNE />
            </a>
            <a href="/Joshua_Lake_CV_2026.pdf" className="btn-outline" target="_blank" rel="noreferrer">
              <span>Download CV</span>
            </a>
            <a href="#contact" className="btn-outline">
              <span>Get in Touch</span>
            </a>
          </div>
        </div>

        <a href="#platforms" className="hero__scroll" aria-label="Scroll down">
          <div className="hero__scroll-mouse">
            <div className="hero__scroll-wheel" />
          </div>
          <span className="hero__scroll-text">Scroll</span>
        </a>
      </section>

      {/* ══ PLATFORMS ══ */}
      <section
        id="platforms"
        className="platforms"
        ref={platformsRef}
        data-section-theme="platforms"
        style={{
          '--nav-bg': 'rgba(10,10,16,0.7)',
          '--nav-text': '#f5f2ec',
          '--nav-accent': '#e8553d',
          '--nav-border': 'rgba(245,242,236,0.08)',
          '--section-bg': '#0c0e16',
        }}
      >
        <div className="platforms-header" aria-hidden="true">
          <span className="platforms-tag">001 — Platforms</span>
        </div>
        <div className="platforms-progress" aria-hidden="true" />
        <div className="platforms-track">
          {PLATFORMS.map((p, i) => (
            <div key={p.id} className="platform-panel">
              <div className="platform-panel__bg">
                {p.image && <img src={p.image} alt="" aria-hidden="true" loading="lazy" />}
              </div>
              <div className="platform-panel__overlay" aria-hidden="true" />
              <div className="platform-panel__content">
                <span className="platform-panel__index" aria-hidden="true">0{i + 1}</span>
                <span className="platform-panel__type">{p.type}</span>
                <h3 className="platform-panel__name">{p.name}</h3>
                <p className="platform-panel__subtitle">{p.subtitle}</p>
                <p className="platform-panel__desc">{p.desc}</p>
                <ul className="platform-panel__specs">
                  {p.specs.map((s) => (
                    <li key={s}>
                      <svg viewBox="0 0 8 8" fill="currentColor" aria-hidden="true">
                        <rect x="2" y="2" width="4" height="4" />
                      </svg>
                      {s}
                    </li>
                  ))}
                </ul>
                {p.link && (
                  <a href={p.link} className="platform-panel__link" target="_blank" rel="noreferrer">
                    <span>Visit Platform</span>
                    <ArrowNE />
                  </a>
                )}
              </div>
              <div className="platform-panel__counter" aria-hidden="true">
                0{i + 1}&nbsp;<span className="slash">/</span>&nbsp;0{PLATFORMS.length}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ══ MISSION CONSOLE ══ */}
      <MissionConsole />

      {/* ══ JNEURAL TERMINAL ══ */}
      <JneuralTerminal />

      {/* ══ ABOUT ══ */}
      <section
        id="about"
        className="about"
        data-section-theme="about"
        style={{
          '--nav-bg': 'rgba(237,232,223,0.75)',
          '--nav-text': '#0a0a0a',
          '--nav-accent': '#e8553d',
          '--nav-border': 'rgba(10,10,10,0.08)',
          '--section-bg': '#ede8df',
        }}
      >
        <div className="about__inner">
          <div className="about__left">
            <span className="about__tag">002 — About</span>
            <h2 className="about__heading">
              Engineering&#8209;first,<br />
              from concept<br />
              through deployment.
            </h2>
          </div>
          <div className="about__right">
            <div className="about__body">
              <p>
                I build autonomous systems that have to work — not in controlled demos, but in
                the field. From early-stage concept through flight-tested hardware, I handle
                the full stack: firmware, autonomy algorithms, simulation, and operational
                infrastructure.
              </p>
              <p>
                The work spans defence, commercial, and research. When a system is operating
                in a GPS-denied corridor at altitude, it can't afford ambiguity. That's the
                standard I build to.
              </p>
            </div>
            <div className="about__stats">
              <div className="stat">
                <span className="stat-num" data-target="6">0</span>
                <span className="stat-plus">+</span>
                <span className="stat-label">years</span>
              </div>
              <div className="stat">
                <span className="stat-num" data-target="25">0</span>
                <span className="stat-plus">+</span>
                <span className="stat-label">systems delivered</span>
              </div>
              <div className="stat">
                <span className="stat-num" data-target="5">0</span>
                <span className="stat-label">active platforms</span>
              </div>
            </div>
            <a href="#work" className="about__cta">
              <span>Selected work</span>
              <ArrowNE />
            </a>
          </div>
        </div>
      </section>

      {/* ══ SERVICES ══ */}
      <section
        id="services"
        className="services"
        data-section-theme="services"
        style={{
          '--nav-bg': 'rgba(8,10,14,0.65)',
          '--nav-text': '#f5f2ec',
          '--nav-accent': '#5b7fff',
          '--nav-border': 'rgba(245,242,236,0.08)',
          '--section-bg': '#080a0e',
        }}
      >
        <div className="services-inner">
          <div className="services-left">
            <span className="services-tag">003 — Services</span>
            <h2 className="services-heading">Engineering<br />Services</h2>
            {/* Decorative vertical timeline SVG */}
            <svg className="services-deco" viewBox="0 0 60 380" fill="none" aria-hidden="true">
              <line x1="30" y1="0" x2="30" y2="380" stroke="currentColor" strokeWidth="0.5" strokeDasharray="3 9" />
              {[0, 63, 126, 190, 253, 316].map((y, i) => (
                <g key={i}>
                  <circle cx="30" cy={y + 22} r="3.5" stroke="currentColor" strokeWidth="1" fill="none" />
                  <line x1="18" y1={y + 22} x2="42" y2={y + 22} stroke="currentColor" strokeWidth="0.5" />
                </g>
              ))}
            </svg>
          </div>
          <div className="services-right">
            {SERVICES.map((s) => (
              <div key={s.num} className="service-row">
                <span className="service-num">{s.num}</span>
                <div className="service-body">
                  <h3 className="service-title">{s.title}</h3>
                  <p className="service-desc">{s.desc}</p>
                  <div className="service-tags">
                    {s.tags.map((t) => <span key={t} className="service-tag">{t}</span>)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ WORK ══ */}
      <section
        id="work"
        className="work"
        data-section-theme="work"
        style={{
          '--nav-bg': 'rgba(8,10,14,0.65)',
          '--nav-text': '#f5f2ec',
          '--nav-accent': '#3d5af1',
          '--nav-border': 'rgba(245,242,236,0.08)',
          '--section-bg': '#080a0e',
        }}
      >
        <div className="work-inner">
          <div className="work-header">
            <span className="work-tag">004 — Work</span>
            <h2 className="work-heading">
              <SplitText type="words" animation="fadeUp" stagger={0.05}>Selected Projects</SplitText>
            </h2>
          </div>
          <div className="projects-list">
            {PROJECTS.map((p, i) => (
              <article
                key={p.num}
                className={`project-card${i % 2 === 1 ? ' project-card--flip' : ''}${p.darkBg ? ' project-card--dark-img' : ''}`}
              >
                <div className="project-card__vis">
                  <img className="project-card__img" src={p.image} alt={p.title} loading="lazy" />
                  <span className="project-card__vis-num" aria-hidden="true">{p.num}</span>
                </div>
                <div className="project-card__body">
                  <span className="project-card__cat">{p.category}</span>
                  <h3 className="project-card__title">{p.title}</h3>
                  <p className="project-card__desc">{p.desc}</p>
                  <div className="project-card__tags">
                    {p.tags.map((t) => <span key={t} className="project-card__tag">{t}</span>)}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ══ CONTACT ══ */}
      <section
        id="contact"
        className="contact"
        data-section-theme="contact"
        style={{
          '--nav-bg': 'rgba(237,232,223,0.75)',
          '--nav-text': '#0a0a0a',
          '--nav-accent': '#3d5af1',
          '--nav-border': 'rgba(10,10,10,0.08)',
          '--section-bg': '#ede8df',
        }}
      >
        <div className="contact__inner">
          <div className="contact__left">
            <span className="contact__tag">005 — Contact</span>
            <h2 className="contact__heading">
              Let's work<br /><em>together.</em>
            </h2>
            <div className="contact-info">
              <div className="contact-info__row">
                <span className="contact-info__label">Email</span>
                <a href="mailto:josh@jneural.net" className="contact-info__value">josh@jneural.net</a>
              </div>
              <div className="contact-info__row">
                <span className="contact-info__label">Location</span>
                <span className="contact-info__value">United Kingdom</span>
              </div>
              <div className="contact-info__row">
                <span className="contact-info__label">Status</span>
                <span className="contact-info__value contact-info__value--available">
                  <span className="avail-dot" aria-hidden="true" />
                  Open to new projects
                </span>
              </div>
            </div>
            <div className="contact-info__socials">
              <a href="https://github.com/Joshua-Lake01" target="_blank" rel="noreferrer" className="social-link" aria-label="Visit GitHub">
                <IconGitHub />
              </a>
              <a href="https://www.linkedin.com/in/joshua-lake-jl2412/" target="_blank" rel="noreferrer" className="social-link" aria-label="Visit LinkedIn">
                <IconLinkedIn />
              </a>
            </div>
          </div>

          <div className="contact__right">
            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="cf-name">Name</label>
                <input id="cf-name" name="name" type="text" placeholder="Your name" value={formData.name} onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))} required autoComplete="name" />
                <span className="form-line" aria-hidden="true" />
              </div>
              <div className="form-group">
                <label htmlFor="cf-email">Email</label>
                <input id="cf-email" name="email" type="email" placeholder="your@email.com" value={formData.email} onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))} required autoComplete="email" />
                <span className="form-line" aria-hidden="true" />
              </div>
              <div className="form-group">
                <label htmlFor="cf-message">Message</label>
                <textarea id="cf-message" name="message" rows={5} placeholder="Tell me about your project..." value={formData.message} onChange={(e) => setFormData((p) => ({ ...p, message: e.target.value }))} required />
                <span className="form-line" aria-hidden="true" />
              </div>
              {formStatus === 'sent' && (
                <p className="form-success">Message sent — I'll be in touch soon.</p>
              )}
              <button type="submit" className="btn-submit" disabled={formStatus === 'sending' || formStatus === 'sent'}>
                <span>{formStatus === 'sending' ? 'Sending...' : formStatus === 'sent' ? 'Sent' : 'Send message'}</span>
                <ArrowNE />
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* ══ FOOTER ══ */}
      <footer className="site-footer">
        <div className="site-footer__inner">
          <span className="site-footer__copy">
            &copy; {new Date().getFullYear()} Lake Software Industries
          </span>
          <nav className="site-footer__nav" aria-label="Footer navigation">
            <a href="#about">About</a>
            <a href="#services">Services</a>
            <a href="#work">Work</a>
            <a href="#platforms">Platforms</a>
            <a href="#contact">Contact</a>
          </nav>
          <a href="#hero" className="site-footer__top" aria-label="Back to top">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <line x1="12" y1="19" x2="12" y2="5" />
              <polyline points="5 12 12 5 19 12" />
            </svg>
          </a>
        </div>
      </footer>

    </main>
  );
};

export default Home;
